export interface ImportDbFieldItem {
  column: string
  label: string
  value: string
  confidential?: boolean
  inferred?: boolean
  autoGenerated?: boolean
}

export interface ImportPreviewRow {
  excelRow: number
  rowLabel: string
  dbFields: ImportDbFieldItem[]
  warnings: string[]
  errors: string[]
  importable: boolean
  /** 入库预览尚未返回该行结果 */
  pending?: boolean
  /** 本批接口超时/中断，不是表格填错 */
  timeout?: boolean
}

export interface ImportPreviewResult {
  totalCount: number
  importableCount: number
  errorCount: number
  icspEnabled?: boolean
  confidentialHint?: string
  rows: ImportPreviewRow[]
}

export interface ImportErrorItem {
  row: number
  message: string
}

export interface ImportCommitResult {
  totalCount: number
  successCount: number
  errorCount: number
  errorDetail: ImportErrorItem[]
  failedExcelBase64?: string
  failedExcelFileName?: string
  failedExcelError?: string
}

/** 分片导入单行 */
export interface ImportChunkRowPayload {
  excelRow: number
  values: Record<string, string>
}

export interface ImportChunkResult {
  successCount: number
  errorCount: number
  successRows: number[]
  errorDetail: ImportErrorItem[]
}

/** 入库预览 / 分片导入超时（万级数据） */
export const OVERSEAS_IMPORT_HTTP_TIMEOUT_MS = 180_000

/** 前端每批提交行数，须 ≤ 后端 ImportChunkParam.MAX_ROWS */
export const OVERSEAS_IMPORT_CHUNK_SIZE = 400

/** 写入并行批次数（人员身份证已 Redis 占位，可安全并发） */
export const OVERSEAS_IMPORT_COMMIT_CONCURRENCY = 6

/**
 * 入库预览分批校验行数。
 * 单批过大时容易被网关/浏览器掐断（常见约 60s），表现为整批「请求超时」。
 */
export const OVERSEAS_IMPORT_PREVIEW_CHUNK_SIZE = 80

/** 入库预览并行批次数（过高会把后几批拖过超时阈值） */
export const OVERSEAS_IMPORT_PREVIEW_CONCURRENCY = 2

/** 入库预览单批超时（毫秒） */
export const OVERSEAS_IMPORT_PREVIEW_HTTP_TIMEOUT_MS = 180_000

/** 超时 / 网关失败时自动重试次数（不含首次） */
export const OVERSEAS_IMPORT_PREVIEW_TRANSIENT_RETRIES = 2

export const OVERSEAS_IMPORT_PREVIEW_TIMEOUT_HINT =
  '本批核对超时，不是表格填错。请点「重新校验异常数据」再试'

/** 导入预热超时（首次建 Redis 索引可能较久） */
export const OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS = 180_000

function chunkNum(v: unknown): number {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function unwrapChunkPayload(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  const inner = o.data
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return o
}

export function parseImportChunkResult(raw: unknown): ImportChunkResult {
  const o = unwrapChunkPayload(raw)
  if (!o) {
    return { successCount: 0, errorCount: 0, successRows: [], errorDetail: [] }
  }
  const successRows: number[] = []
  const successRaw = o.successRows ?? o.success_rows
  if (Array.isArray(successRaw)) {
    for (const n of successRaw) {
      const row = chunkNum(n)
      if (row > 0) successRows.push(row)
    }
  }
  const errorDetail: ImportErrorItem[] = []
  const errorRaw = o.errorDetail ?? o.error_detail
  if (Array.isArray(errorRaw)) {
    for (const item of errorRaw) {
      if (item == null || typeof item !== 'object' || Array.isArray(item)) continue
      const e = item as Record<string, unknown>
      errorDetail.push({
        row: chunkNum(e.row ?? e.excelRow ?? e.excel_row),
        message: e.message != null ? String(e.message) : '',
      })
    }
  }
  const rawSuccessCount = o.successCount ?? o.success_count
  const rawErrorCount = o.errorCount ?? o.error_count
  const parsedSuccess = chunkNum(rawSuccessCount)
  const parsedError = chunkNum(rawErrorCount)
  return {
    successCount:
      rawSuccessCount == null || rawSuccessCount === '' ? successRows.length : parsedSuccess,
    errorCount: rawErrorCount == null || rawErrorCount === '' ? errorDetail.length : parsedError,
    successRows,
    errorDetail,
  }
}

function pickPreviewStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v != null && String(v).trim() !== '') return String(v)
  }
  return ''
}

export function parseImportPreviewResult(raw: unknown): ImportPreviewResult {
  const o = unwrapChunkPayload(raw)
  if (!o) {
    return { totalCount: 0, importableCount: 0, errorCount: 0, rows: [] }
  }
  const rows: ImportPreviewRow[] = []
  if (Array.isArray(o.rows)) {
    for (const item of o.rows) {
      if (!item || typeof item !== 'object') continue
      const r = item as Record<string, unknown>
      const dbFieldsRaw = r.dbFields
      const dbFields: ImportDbFieldItem[] = []
      if (Array.isArray(dbFieldsRaw)) {
        for (const f of dbFieldsRaw) {
          if (!f || typeof f !== 'object') continue
          const x = f as Record<string, unknown>
          dbFields.push({
            column: pickPreviewStr(x, 'column'),
            label: pickPreviewStr(x, 'label'),
            value: pickPreviewStr(x, 'value'),
            confidential: x.confidential === true,
            inferred: x.inferred === true,
            autoGenerated: x.autoGenerated === true,
          })
        }
      }
      rows.push({
        excelRow: chunkNum(r.excelRow ?? r.excel_row),
        rowLabel: pickPreviewStr(r, 'rowLabel') || '（无名称）',
        dbFields,
        warnings: Array.isArray(r.warnings) ? r.warnings.map(String) : [],
        errors: Array.isArray(r.errors) ? r.errors.map(String) : [],
        importable: r.importable === true || r.importable === 'true' || r.importable === 1,
        pending: false,
      })
    }
  }
  return {
    totalCount: chunkNum(o.totalCount) || rows.length,
    importableCount: chunkNum(o.importableCount),
    errorCount: chunkNum(o.errorCount),
    icspEnabled: o.icspEnabled === true,
    confidentialHint: pickPreviewStr(o, 'confidentialHint') || undefined,
    rows,
  }
}
